import React, { useCallback, useEffect, useState } from 'react';
import { Input, Spinner } from 'reactstrap';
import cs_analysis_fetch from '../../../utils/cs_analysis_fetch';
import {
    GROUP_ACCESS_NOTE, addable_org_users, channel_member_path, channel_members_path, fetch_org_members, is_general_group,
    member_link_chip, partition_group_members,
} from './member_links';

/**
 * "Members" drawer for a group channel (CLIENT_GROUP) — Nikhil 2026-09-13.
 * Lists the portal users with access to the group (kind CLIENT_USER, with
 * their WhatsApp link chip), the WhatsApp members the bridge knows, and the
 * Infurnia team; adds a portal user from the org's user list (the same
 * infurnia-main-backed list the WhatsApp mapping picker uses, via the org
 * members endpoint) and removes one.
 *
 * general is visible to every user of the org, so its add picker is off;
 * other groups only show to their members.
 */

function LinkChip({ member }) {
    const chip = member_link_chip(member);
    if (!chip) return null;
    return (
        <span title={chip.title} style={{ fontSize: 9.5, color: chip.fg, background: chip.bg, border: '1px solid ' + chip.bd, borderRadius: 8, padding: '0 6px', fontWeight: 600, whiteSpace: 'nowrap' }}>
            {chip.kind === 'self' ? '✓ ' : ''}{chip.label}
        </span>
    );
}

const SECTION = { fontSize: 10.5, letterSpacing: 0.7, textTransform: 'uppercase', color: '#8a97a5', fontWeight: 700, margin: '14px 0 4px' };
const ROW = { display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: '1px solid #f4f6f8', fontSize: 12.5 };

export default function GroupMembersDrawer({ channel_id, store_id, conversation, on_close }) {
    const [members, set_members] = useState(null);
    const [org_users, set_org_users] = useState(null); // loaded when the picker opens
    const [adding, set_adding] = useState(false);
    const [q, set_q] = useState('');
    const [busy, set_busy] = useState(false);
    const [err, set_err] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await cs_analysis_fetch({ path: channel_members_path(channel_id) });
            set_members((r && r.members) || []);
        } catch (e) {
            set_members([]);
            set_err((e && e.message) || 'members unavailable');
        }
    }, [channel_id]);
    useEffect(() => { set_members(null); set_err(null); load(); }, [load]);

    useEffect(() => {
        if (!adding || org_users !== null || !store_id) return undefined;
        let dead = false;
        fetch_org_members(cs_analysis_fetch, store_id)
            .then((r) => { if (!dead) set_org_users((r && r.orgUsers) || []); })
            .catch((e) => { if (!dead) { set_org_users([]); set_err((e && e.message) || 'org users unavailable'); } });
        return () => { dead = true; };
    }, [adding, org_users, store_id]);

    useEffect(() => {
        const h = (e) => { if (e.key === 'Escape') on_close(); };
        window.addEventListener('keydown', h);
        return () => window.removeEventListener('keydown', h);
    }, [on_close]);

    const act = async (fn) => {
        if (busy) return;
        set_busy(true);
        set_err(null);
        try { await fn(); await load(); } catch (e) { set_err((e && e.message) || 'failed'); }
        set_busy(false);
    };
    const add = (u) => act(async () => {
        await cs_analysis_fetch({ path: channel_members_path(channel_id), method: 'POST', body: { clientUserId: String(u.id) } });
        set_q('');
        set_adding(false);
    });
    const remove = (m) => {
        if (!window.confirm(`Remove ${m.name || 'this user'} from the group? They stop seeing it in the portal.`)) return;
        act(() => cs_analysis_fetch({ path: channel_member_path(channel_id, m.clientUserId), method: 'DELETE' }));
    };

    const parts = partition_group_members(members);
    const general = is_general_group(conversation);
    const matches = adding ? addable_org_users(org_users || [], members, q).slice(0, 25) : [];
    const title = conversation && conversation.groupKey ? conversation.groupKey : 'group';

    return (
        <div onClick={on_close} style={{ position: 'fixed', inset: 0, background: 'rgba(20,25,30,0.5)', zIndex: 1500, display: 'flex', justifyContent: 'flex-end' }}>
            <div onClick={(e) => e.stopPropagation()} role='dialog' aria-label='Group members'
                style={{ width: 'min(480px, 92vw)', height: '100%', background: '#fff', boxShadow: '-4px 0 18px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #eef1f4' }}>
                    <b style={{ fontSize: 14 }}><span role='img' aria-label='members'>👥</span> Members · {title}</b>
                    {conversation && conversation.clientName ? <span style={{ fontSize: 12, color: '#7f8c8d', marginLeft: 8 }}>{conversation.clientName}</span> : null}
                    <span onClick={on_close} title='Close (Esc)' style={{ marginLeft: 'auto', cursor: 'pointer', color: '#95a5a6', fontSize: 16, lineHeight: 1 }}>✕</span>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '4px 14px 14px' }}>
                    <div style={{ fontSize: 11.5, color: '#7f8c8d', background: '#f6f8f9', border: '1px solid #e8ecef', borderRadius: 6, padding: '5px 8px', marginTop: 10 }}>
                        {GROUP_ACCESS_NOTE}
                    </div>
                    {members === null ? <div style={{ marginTop: 12 }}><Spinner size='sm' /> loading…</div> : (
                        <>
                            <div style={{ ...SECTION, display: 'flex', alignItems: 'center', gap: 8 }}>
                                Portal users with access{parts.portal.length ? ` (${parts.portal.length})` : ''}
                                {general ? (
                                    <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400, color: '#a4b0bb' }}>everyone in the org — nothing to add</span>
                                ) : (
                                    <button type='button' disabled={busy}
                                        onClick={() => { set_adding((v) => !v); set_q(''); }}
                                        style={{ border: 'none', background: 'transparent', color: '#2f6fed', cursor: 'pointer', fontSize: 11, padding: 0, fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>
                                        {adding ? '× cancel' : '+ add a portal user'}
                                    </button>
                                )}
                            </div>
                            {adding && !general ? (
                                <div style={{ marginBottom: 6 }}>
                                    <Input bsSize='sm' value={q} autoFocus placeholder="Search the org's users (name / email)…"
                                        onChange={(e) => set_q(e.target.value)}
                                        onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Escape') { e.preventDefault(); set_adding(false); } }}
                                        disabled={busy} style={{ fontSize: 12 }} />
                                    <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #e8ecef', borderTop: 'none', borderRadius: '0 0 6px 6px' }}>
                                        {org_users === null ? <div style={{ padding: '6px 10px', fontSize: 12, color: '#95a5a6' }}>Loading the org's users…</div>
                                            : matches.length === 0 ? (
                                                <div style={{ padding: '6px 10px', fontSize: 12, color: '#95a5a6' }}>
                                                    {!store_id ? 'This conversation carries no org.' : q.trim() ? 'No org user matches.' : 'Every org user is already in this group.'}
                                                </div>
                                            ) : matches.map((u) => (
                                                <button key={u.id} type='button' disabled={busy} onClick={() => add(u)}
                                                    style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', borderBottom: '1px solid #f0f2f5', background: '#fff', padding: '5px 10px', cursor: 'pointer', fontSize: 12 }}>
                                                    <b style={{ color: '#2c3e50' }}>{u.name || u.email || u.id}</b>
                                                    {u.email && u.name ? <span style={{ color: '#95a5a6', marginLeft: 6 }}>{u.email}</span> : null}
                                                </button>
                                            ))}
                                    </div>
                                </div>
                            ) : null}
                            {parts.portal.length === 0 ? (
                                <div style={{ fontSize: 12, color: '#95a5a6' }}>{general ? 'Every user of the org can open this group.' : 'No portal user has access yet.'}</div>
                            ) : parts.portal.map((m) => (
                                <div key={`p:${m.clientUserId}`} style={ROW}>
                                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name || m.clientUserId}</span>
                                    <LinkChip member={m} />
                                    {!general ? (
                                        <button type='button' title='Remove from this group' disabled={busy} onClick={() => remove(m)}
                                            style={{ border: 'none', background: 'transparent', color: '#c0392b', cursor: 'pointer', fontSize: 13, padding: '0 4px', lineHeight: 1 }}>✕</button>
                                    ) : null}
                                </div>
                            ))}

                            <div style={SECTION}>WhatsApp members{parts.whatsapp.length ? ` (${parts.whatsapp.length})` : ''}</div>
                            {parts.whatsapp.length === 0 ? (
                                <div style={{ fontSize: 12, color: '#95a5a6' }}>None known — they appear once the group's WhatsApp bridge is on.</div>
                            ) : parts.whatsapp.map((m, i) => (
                                <div key={`w:${i}:${m.name}`} style={ROW}>
                                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</span>
                                    <span style={{ fontSize: 10.5, color: '#95a5a6' }}>{m.kind === 'USER' ? 'mapped' : 'guest'}</span>
                                    <LinkChip member={m} />
                                </div>
                            ))}

                            <div style={SECTION}>Infurnia team{parts.team.length ? ` (${parts.team.length})` : ''}</div>
                            {parts.team.map((m, i) => (
                                <div key={`t:${i}:${m.name}`} style={ROW}>
                                    <span style={{ flex: 1 }}>{m.name}</span>
                                    <span style={{ fontSize: 10.5, color: '#95a5a6' }}>{m.kind === 'REP' ? 'rep (WhatsApp)' : 'staff'}</span>
                                </div>
                            ))}
                        </>
                    )}
                    {err ? <div style={{ color: '#c0392b', fontSize: 11.5, marginTop: 8 }}>{err}</div> : null}
                    <div style={{ fontSize: 11, color: '#a4b0bb', marginTop: 14 }}>
                        WhatsApp mapping (guest → user) lives under ⋯ on the conversation row and in the org dashboard.
                    </div>
                </div>
            </div>
        </div>
    );
}
